import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, HeartPulse, Droplet, Thermometer, Wind, Phone, AlertTriangle,
  ChevronUp, ChevronDown, BellRing, ClipboardPlus, ArrowRightCircle,
  Sparkles, Stethoscope,
} from "lucide-react";
import { VITAL_META, STATUS_COLORS, RISK_COLORS, timeAgo } from "@/pages/rpm/constants";

const ICONS = {
  bp_systolic: Activity, bp_diastolic: Activity, heart_rate: HeartPulse,
  spo2: Droplet, temperature: Thermometer, respiratory_rate: Wind,
};

function RiskBadge({ level, label, prefix }) {
  const c = RISK_COLORS[level] || RISK_COLORS.stable;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}40` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
      {prefix ? `${prefix} ` : ""}{label || c.label}
    </span>
  );
}

function VitalTile({ tile }) {
  const Icon = ICONS[tile.vital_type] || Activity;
  const meta = VITAL_META[tile.vital_type] || {};
  const c = STATUS_COLORS[tile.status] || STATUS_COLORS.missing;
  return (
    <div className="rounded-lg border-2 p-2.5" style={{ background: c.bg, borderColor: c.border + "55" }}
      data-testid={`vital-tile-${tile.vital_type}`}>
      <div className="flex items-center justify-between mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: c.text }} />
        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: c.text }}>{meta.short}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-base font-bold" style={{ color: c.text }}>
          {tile.value != null ? tile.value : "—"}
        </span>
        <span className="text-[10px]" style={{ color: c.text + "BB" }}>{tile.unit || meta.unit}</span>
      </div>
      <div className="text-[10px] mt-0.5" style={{ color: c.text + "AA" }}>{timeAgo(tile.taken_at)}</div>
    </div>
  );
}

export default function PatientMonitoringCard({ card, onAck, onIntervention, onEscalate }) {
  const navigate = useNavigate();
  const news2 = card.news2 || {};
  const ai = card.ai_risk || {};
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 hover:shadow-md transition-shadow"
      data-testid={`rpm-card-${card.mrn}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-semibold text-[#1F2937] truncate">{card.name}</h3>
            <span className="text-[11px] font-mono text-[#6B7280]">{card.mrn}</span>
          </div>
          <div className="text-[11px] text-[#6B7280] flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{card.age}y · {card.gender}</span>
            {card.primary_diagnosis && <span title={card.primary_diagnosis}>{card.primary_diagnosis}</span>}
            {card.facility_name && <span>· {card.facility_name}</span>}
          </div>
          <div className="text-[10px] text-[#94A3B8] mt-0.5">
            Nurse: <strong className="text-[#475569]">{card.nurse_name || "—"}</strong>
            {card.physician_name && <> · MD: <strong className="text-[#475569]">{card.physician_name}</strong></>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <RiskBadge level={card.overall_status} prefix="" />
          <div className="flex items-center gap-1">
            <RiskBadge level={news2.risk_level === "medium" ? "moderate" : news2.risk_level} label={`NEWS2 ${news2.score}`} />
          </div>
          <RiskBadge level={ai.risk_level} label={`AI ${ai.risk_level}`} prefix="" />
        </div>
      </div>

      {/* Vital tiles */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        {(card.vital_tiles || []).map((t) => <VitalTile key={t.vital_type} tile={t} />)}
      </div>

      {/* Recommended action */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3"
        style={{ background: (RISK_COLORS[card.overall_status] || RISK_COLORS.stable).bg }}>
        <Sparkles className="w-3.5 h-3.5" style={{ color: (RISK_COLORS[card.overall_status] || RISK_COLORS.stable).text }} />
        <span className="text-[11px] uppercase font-semibold tracking-wider"
          style={{ color: (RISK_COLORS[card.overall_status] || RISK_COLORS.stable).text }}>Action:</span>
        <span className="text-xs font-medium" style={{ color: (RISK_COLORS[card.overall_status] || RISK_COLORS.stable).text }}>
          {card.recommended_action}
        </span>
      </div>

      {/* Indicators */}
      <div className="flex items-center gap-3 text-[11px] text-[#6B7280] mb-3">
        {card.active_alerts > 0 && (
          <span className="inline-flex items-center gap-1 text-[#EF4444]">
            <BellRing className="w-3 h-3" /> {card.active_alerts} alert{card.active_alerts > 1 ? "s" : ""}
          </span>
        )}
        {card.missing_count > 0 && (
          <span className="inline-flex items-center gap-1 text-[#F59E0B]">
            <AlertTriangle className="w-3 h-3" /> {card.missing_count} missing
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: card.device_status === "active" ? "#10B981" : "#94A3B8" }} />
          Device {card.device_status}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 pt-3 border-t border-[#E5E7EB]">
        <button onClick={() => navigate(`/rpm/patients/${card.patient_id}`)}
          className="flex-1 text-xs font-medium text-[#0B7E8C] border border-[#0B7E8C]/30 hover:bg-[#0B7E8C]/8 rounded-md px-2 py-1.5 inline-flex items-center justify-center gap-1"
          data-testid={`rpm-card-details-${card.mrn}`}>
          <Stethoscope className="w-3.5 h-3.5" /> RPM Details
        </button>
        <button onClick={() => navigate(`/patients/${card.patient_id}`)}
          className="text-xs text-[#6B7280] hover:text-[#0B7E8C] border border-[#E5E7EB] hover:border-[#0B7E8C]/30 rounded-md px-2 py-1.5"
          data-testid={`rpm-card-profile-${card.mrn}`}>
          Profile
        </button>
        {onAck && card.active_alerts > 0 && (
          <button onClick={() => onAck(card)} className="text-xs text-[#6B7280] hover:text-[#0B7E8C] border border-[#E5E7EB] rounded-md px-2 py-1.5"
            data-testid={`rpm-card-ack-${card.mrn}`} title="Acknowledge alerts">
            <BellRing className="w-3.5 h-3.5" />
          </button>
        )}
        {onIntervention && (
          <button onClick={() => onIntervention(card)} className="text-xs text-[#6B7280] hover:text-[#0B7E8C] border border-[#E5E7EB] rounded-md px-2 py-1.5"
            data-testid={`rpm-card-intervention-${card.mrn}`} title="Add intervention">
            <ClipboardPlus className="w-3.5 h-3.5" />
          </button>
        )}
        {onEscalate && card.overall_status !== "stable" && (
          <button onClick={() => onEscalate(card)} className="text-xs text-[#EF4444] hover:bg-[#EF444412] border border-[#EF444433] rounded-md px-2 py-1.5"
            data-testid={`rpm-card-escalate-${card.mrn}`} title="Escalate">
            <ArrowRightCircle className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
